import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ThemePicker } from "../../design-system/components/ThemePicker";
import { AllAppsButton } from "../../design-system/components/AllAppsButton";

export interface RailItem {
  id: string;
  icon: LucideIcon;
  label: string;
  path: string;
  badge?: number;
}

export interface ShellTab {
  label: string;
  path: string;
  end?: boolean;
}

interface LegacyAppShellProps {
  /** Short mark in the rail brand tile (e.g. S, B2B, POS). */
  brand: string;
  /** Surface name under the brand (e.g. Seller, Warehouse). */
  surface: string;
  homePath: string;
  items: RailItem[];
  activeId: string;
  tabs?: ShellTab[];
  /** Optional title shown when there are no tabs. */
  title?: string;
  identity?: { name: string; subtitle?: string };
  tools?: ReactNode;
  children: ReactNode;
}

/** Shared MCE-aligned chrome for seller / B2B / POS / warehouse. */
export function LegacyAppShell({
  brand, surface, homePath, items, activeId, tabs, title, identity, tools, children,
}: LegacyAppShellProps) {
  const navigate = useNavigate();

  return (
    <div className="app app-mce">
      <nav className="rail" aria-label={surface}>
        <button
          type="button"
          className="rail-brand"
          title={surface}
          onClick={() => navigate(homePath)}
        >
          <span className="rail-brand-mark">{brand}</span>
          <span className="rail-brand-meta">
            <span className="rail-brand-name">SYNCORB</span>
            <span className="rail-brand-surface">{surface}</span>
          </span>
        </button>

        <div className="rail-nav">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = activeId === item.id;
            return (
              <button
                key={item.id}
                type="button"
                className={`rbtn${isActive ? " active" : ""}`}
                onClick={() => navigate(item.path)}
                title={item.label}
              >
                <Icon size={18} strokeWidth={1.9} />
                <span className="rb-label">{item.label}</span>
                {item.badge != null && item.badge > 0 && (
                  <span className="rb-badge">{item.badge > 99 ? "99+" : item.badge}</span>
                )}
                <span className="rb-tip">{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="rail-sep" />
        <button
          type="button"
          className="rail-user"
          title={identity?.name ?? "Account"}
          onClick={() => navigate("/apps")}
        >
          {(identity?.name ?? surface)[0]?.toUpperCase()}
        </button>
      </nav>

      <div className="shell">
        <header className="topbar">
          <div className="topbar-left">
            {tabs && tabs.length > 0 ? (
              <div className="topbar-tabs">
                {tabs.map((tab) => (
                  <NavLink
                    key={tab.path}
                    to={tab.path}
                    end={tab.end ?? tab.path === homePath}
                    className={({ isActive }) => `ttab${isActive ? " active" : ""}`}
                  >
                    {tab.label}
                  </NavLink>
                ))}
              </div>
            ) : (
              <div className="topbar-title">
                <span className="topbar-kicker">{surface}</span>
                <h1>{title ?? surface}</h1>
              </div>
            )}
          </div>

          <div className="topbar-tools">
            <ThemePicker compact />
            <AllAppsButton />
            {tools}
            {identity && (
              <div className="topbar-identity">
                <div className="topbar-avatar">{identity.name[0]?.toUpperCase()}</div>
                <div className="topbar-identity-text">
                  <div className="topbar-identity-name">{identity.name}</div>
                  {identity.subtitle && <div className="topbar-identity-sub">{identity.subtitle}</div>}
                </div>
              </div>
            )}
          </div>
        </header>

        <div className="console-main">
          <div className="console-content">{children}</div>
        </div>
      </div>
    </div>
  );
}
